import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Wrench } from 'lucide-react';
import { type ChangeManagement } from '@/hooks/useChangeManagements';
import { type MaintenanceWindow } from '@/hooks/useMaintenanceWindows';
import { CM_STATE_DOT, cmStateLabel } from '@/lib/cmState';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CMCalendarProps {
  cms: ChangeManagement[];
  windows: MaintenanceWindow[];
  /** Open the create flow pre-scheduled for this day. */
  onScheduleDay: (day: Date) => void;
}

function parse(t: string | null): Date | null {
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Does a maintenance window have an occurrence on `day`? Handles one-off + recurring. */
function windowOnDay(w: MaintenanceWindow, day: Date): boolean {
  const starts = parse(w.startsAt);
  if (!starts) return false;
  const until = parse(w.recurrenceUntil);
  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);
  if (w.recurrence === 'none') {
    const ends = parse(w.endsAt) ?? starts;
    return starts <= dayEnd && ends >= dayStart; // interval overlaps the day
  }
  // recurring: must be on/after the first occurrence and before the until-bound
  if (dayEnd < startOfDay(starts)) return false;
  if (until && dayStart > endOfDay(until)) return false;
  if (w.recurrence === 'weekly') return day.getDay() === starts.getDay();
  return true; // daily
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Month calendar of scheduled changes + maintenance windows; click a day to schedule. */
export default function CMCalendar({ cms, windows, onScheduleDay }: CMCalendarProps) {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(month)),
        end: endOfWeek(endOfMonth(month)),
      }),
    [month],
  );

  // Index scheduled CMs by day (yyyy-MM-dd) once.
  const cmsByDay = useMemo(() => {
    const map = new Map<string, ChangeManagement[]>();
    for (const cm of cms) {
      const start = parse(cm.startTime);
      if (!start) continue;
      const key = format(start, 'yyyy-MM-dd');
      const arr = map.get(key) ?? [];
      arr.push(cm);
      map.set(key, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (parse(a.startTime)?.getTime() ?? 0) - (parse(b.startTime)?.getTime() ?? 0));
    }
    return map;
  }, [cms]);

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      {/* Header: month nav */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold tabular-nums">{format(month, 'MMMM yyyy')}</h2>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => { setMonth(startOfMonth(new Date())); }}>
            Today
          </Button>
          <Button variant="outline" size="icon" aria-label="Previous month" onClick={() => { setMonth((m) => addMonths(m, -1)); }}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" aria-label="Next month" onClick={() => { setMonth((m) => addMonths(m, 1)); }}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-px mb-px">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-[11px] font-medium text-muted-foreground text-center py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-px bg-border-subtle rounded-md overflow-hidden">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayCms = cmsByDay.get(key) ?? [];
          const dayWindows = windows.filter((w) => windowOnDay(w, day));
          const inMonth = isSameMonth(day, month);
          const today = isToday(day);
          return (
            <div
              key={key}
              className={cn(
                'group relative bg-card min-h-[92px] p-1.5 flex flex-col gap-1',
                !inMonth && 'bg-muted/30',
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'text-xs tabular-nums',
                    today
                      ? 'flex items-center justify-center w-5 h-5 rounded-full bg-brand text-white font-semibold'
                      : inMonth
                        ? 'text-foreground'
                        : 'text-muted-foreground',
                  )}
                >
                  {format(day, 'd')}
                </span>
                <button
                  type="button"
                  aria-label={`Schedule a change on ${format(day, 'PP')}`}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-foreground transition-opacity"
                  onClick={() => { onScheduleDay(day); }}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              {dayWindows.length > 0 ? (
                <div
                  className="flex items-center gap-1 text-[10px] text-sev-5 bg-sev-5/10 rounded px-1 py-0.5"
                  title={dayWindows.map((w) => w.name).join(', ')}
                >
                  <Wrench className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">
                    {dayWindows.length === 1 ? dayWindows[0].name : `${String(dayWindows.length)} windows`}
                  </span>
                </div>
              ) : null}

              {dayCms.slice(0, 3).map((cm) => (
                <Link
                  key={cm.id}
                  to={`/change-management/${cm.id}`}
                  title={`${cm.title} — ${cmStateLabel(cm.state)}`}
                  className="flex items-center gap-1 min-w-0 text-[11px] hover:underline"
                >
                  <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', CM_STATE_DOT[cm.state])} aria-hidden="true" />
                  <span className="truncate">{cm.title}</span>
                </Link>
              ))}
              {dayCms.length > 3 ? (
                <span className="text-[10px] text-muted-foreground">+{dayCms.length - 3} more</span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sev-5" />Approved/scheduled</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand" />In progress</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sev-3" />Awaiting approval</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sev-4" />Completed</span>
        <span className="flex items-center gap-1"><Wrench className="w-2.5 h-2.5 text-sev-5" />Maintenance window</span>
      </div>
    </div>
  );
}
